import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '';

// Setup axios defaults
axios.defaults.baseURL = API_URL;

// Interceptor to add token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('karaoke_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor to handle 401
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('karaoke_token');
      localStorage.removeItem('karaoke_user');
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('karaoke_token');
    const savedUser = localStorage.getItem('karaoke_user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Verify token is still valid
        axios.get('/api/auth/me')
          .then(res => setUser(res.data.user))
          .catch(() => logout())
          .finally(() => setLoading(false));
      } catch {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password, captchaToken) => {
    const res = await axios.post('/api/auth/login', { email, password, captchaToken });
    const { token, user: userData } = res.data;
    localStorage.setItem('karaoke_token', token);
    localStorage.setItem('karaoke_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (data) => {
    const res = await axios.post('/api/auth/register', data);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('karaoke_token');
    localStorage.removeItem('karaoke_user');
    setUser(null);
    axios.post('/api/auth/logout').catch(() => {});
  }, []);

  const updateProfile = useCallback(async (data) => {
    const res = await axios.put('/api/auth/profile', data);
    const updatedUser = { ...user, ...res.data.user };
    setUser(updatedUser);
    localStorage.setItem('karaoke_user', JSON.stringify(updatedUser));
    return updatedUser;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, register, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export { axios };
